import React, { Component } from "react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ReferenceDot,
} from "recharts";

import {
  CustomizedXAxisTickTime,
  CustomizedYAxisTickPercentage,
  Metric,
  StandardTooltip,
  Error,
  isDateFromDataInISOFormat,
  formatDateFromData,
} from "../../Utils/Utils.js";
import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsLineLabel,
  InsightsTickIndicator,
} from "../../Utils/InsightsUtils.js";
import { XRayTooltip } from "../../Utils/XRayTooltip/XRayTooltip.js";
import numeral from "numeraljs";

import { getXrayTooltipPosition } from "../../../helpers/DOM.js";

class NewReturning extends Component {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  getExportData = () => ({
    data: this.props.data.data,
    opts: {
      type: "chart",
      series: {
        format: "percentage",
      },
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.data),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  render() {
    const { data, title } = this.props;
    const { widgetsInsights } = this.state;

    const widget_data = data.data;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={400} />;
    }

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = formatDateFromData(widget_data);

    return (
      <div>
        <Metric {...data.new_returning} type={"percentage"} size={"md"} />

        <br />
        <div className="d-flex mt-2" ref={this.divPosition}>
          <ResponsiveContainer height={400} width="100%">
            <AreaChart
              data={preparedData}
              margin={{ top: 12, right: 0, bottom: 20, left: 0 }}
              isAnimationActive={false}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              onClick={(d) => {
                const indicator = "newReturning";
                if (d !== null) {
                  d.indicator = indicator;

                  this.setState({
                    tooltip: { ...d, show: true },
                  });
                } else {
                  this.closeTooltip();
                }
              }}
            >
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                tick={<CustomizedXAxisTickTime />}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTickPercentage />}
              />
              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              <header className="standard-tooltip__header">
                                <span className="standard-tooltip__key-header">
                                  Date:
                                </span>
                                <span className="standard-tooltip__value-heade">
                                  {tooltip_data.date}
                                </span>
                              </header>

                              <main className="standard-tooltip__main">
                                <div className="standard-tooltip__item">
                                  <span className="standard-tooltip__key">
                                    {payload[0] && (
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{ background: payload[0].color }}
                                      ></span>
                                    )}
                                    New
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {numeral(tooltip_data.value).format("0.0%")}
                                  </span>
                                </div>
                                {widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}

              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              <Area
                type="monotone"
                dataKey="value"
                fillOpacity={0.2}
                fill="#f89481"
                stroke="#fc6d52"
                isAnimationActive={false}
              >
                {this.state.widgetsInsights.length > 0 && (
                  <LabelList
                    dataKey="date"
                    content={
                      <InsightsLineLabel
                        height={350}
                        data={preparedData}
                        insights={this.state.widgetsInsights}
                        showIndicator={!this.state.tooltip.show}
                        node={this.node}
                      />
                    }
                  />
                )}
              </Area>

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => (
                  <ReferenceDot
                    x={o.date}
                    y={0}
                    key={title + index}
                    ifOverflow="extendDomain"
                    shape={
                      <InsightsTickIndicator
                        insights={widgetsInsights}
                        date={o.date}
                        node={this.node}
                      />
                    }
                  />
                ))}
            </AreaChart>
          </ResponsiveContainer>
          {this.state.tooltip.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: this.state.tooltip.activeCoordinate.y + 100 + "px",
                left:
                  this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                  this.state.width
                    ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                    : this.state.tooltip.activeCoordinate.x -
                      getXrayTooltipPosition(
                        this.divPosition.current,
                        this.state.tooltip.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltip.show ? 1.0 : 0.0,
                visibility: this.state.tooltip.show ? "visible" : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltip}
                close={this.closeTooltip.bind(this)}
                insights={widgetsInsights}
              />
            </div>
          ) : null}
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default NewReturning;
